// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import classnames from 'classnames';

import {
  DRIVER_BEHAVIOURS,
  DRIVER_BEHAVIOUR_TYPES,
  DRIVER_BEHAVIOUR_PARAMS,
} from 'utils/library-utils';

import InputLocation from 'components/form/InputLocation';
import { roundCoordinate } from 'utils/map-utils';

type Props = {
  defaultValue?: Object,
  disableLoginFields?: boolean,
};

type State = {
  value: Object,
  driverIDValid: boolean,
  imeiValid: boolean,
  locationValid: boolean,
}

const NUMERICAL_FIELDS = ['latitude', 'longitude'];

export default class DriverForm extends Component {
  static defaultProps: Object = {
    defaultValue: {
      behaviour: {
        type: DRIVER_BEHAVIOURS.MANUAL,
        params: {},
      },
    },
    disableLoginFields: false,
  };
  constructor(props: Props) {
    super(props);
    const value: Object = _.cloneDeep(this.props.defaultValue || {});
    NUMERICAL_FIELDS.forEach((field) => {
      if (_.isNumber(value[field])) {
        value[field] = value[field].toString();
      }
    });
    if (value.behaviour.params) {
      DRIVER_BEHAVIOUR_PARAMS[value.behaviour.type].forEach((fieldObject) => {
        value.behaviour.params[fieldObject.name] = fieldObject.renderValue(
          value.behaviour.params[fieldObject.name]);
      });
    }
    this.state = {
      value,
      driverIDValid: true,
      imeiValid: true,
      locationValid: true,
    };
  }

  state: State;

  componentDidMount() {
    navigator.geolocation.getCurrentPosition((position) => {
      if (this.state.value.latitude == null && this.state.value.longitude == null) {
        this.setState({
          value: _.merge(this.state.value, {
            latitude: roundCoordinate(position.coords.latitude),
            longitude: roundCoordinate(position.coords.longitude),
          }),
        }, () => {
          this.setState({ locationValid: this.location.isValid() });
        });
      }
    });
  }

  getValue() {
    const value = _.cloneDeep(this.state.value);
    NUMERICAL_FIELDS.forEach((field) => {
      if (value[field]) {
        value[field] = parseFloat(value[field]);
      }
    });
    const behaviourType = _.get(this.state.value, 'behaviour.type');
    if (value.behaviour.params) {
      DRIVER_BEHAVIOUR_PARAMS[behaviourType].forEach((fieldObject) => {
        const fieldName = fieldObject.name;
        if (value.behaviour.params[fieldName] !== '' &&
          value.behaviour.params[fieldName] != null) {
          value.behaviour.params[fieldName] =
            fieldObject.processValue(value.behaviour.params[fieldName]);
        } else {
          delete value.behaviour.params[fieldName];
        }
      });
    }
    return value;
  }

  isValid() {
    const driverIDValid = this.state.value.cohortDriverID != null &&
      this.state.value.cohortDriverID !== '';
    const imeiValid = this.state.value.imei != null &&
      this.state.value.imei !== '';
    const locationValid = this.location.isValid();
    this.setState({
      driverIDValid,
      imeiValid,
      locationValid,
    });
    return driverIDValid && imeiValid && locationValid;
  }

  props: Props;
  location: InputLocation;

  render() {
    const behaviourType = _.get(this.state.value, 'behaviour.type');
    return (
      <div role="form">
        <div className="notification is-warning" role="alert">
          These drivers have to exist in the driver databases of the respective environments.
        </div>
        <div className="control is-grouped">
          <p className="control is-expanded">
            <label className="label" htmlFor="name">Name</label>
            <input
              className="input"
              type="text"
              id="name"
              placeholder="For your own reference. E.g. Hannah Test Account"
              value={this.state.value.name || ''}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    name: event.target.value,
                  },
                }));
              }}
            />
          </p>
        </div>
        <div className="control is-grouped">
          <p className="control is-expanded">
            <label className="label" htmlFor="driver-id">Driver ID</label>
            <input
              className={classnames('input', { 'is-danger': !this.state.driverIDValid })}
              type="text"
              id="driver-id"
              value={this.state.value.cohortDriverID || ''}
              disabled={this.props.disableLoginFields}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    cohortDriverID: event.target.value,
                  },
                }), () => {
                  this.setState({
                    driverIDValid: this.state.value.cohortDriverID != null &&
                      this.state.value.cohortDriverID !== '',
                  });
                });
              }}
            />
          </p>
          <p className="control is-expanded">
            <label className="label" htmlFor="imei">IMEI</label>
            <input
              className={classnames('input', { 'is-danger': !this.state.imeiValid })}
              type={this.props.disableLoginFields ? 'password' : 'text'}
              id="imei"
              value={this.props.disableLoginFields ?
                'REDACTEDIMEINUM' : (this.state.value.imei || '')
              }
              disabled={this.props.disableLoginFields}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    imei: event.target.value,
                  },
                }), () => {
                  this.setState({
                    imeiValid: this.state.value.imei != null &&
                      this.state.value.imei !== '',
                  });
                });
              }}
            />
          </p>
          <p className="control is-expanded">
            <label className="label" htmlFor="device-id">Device ID</label>
            <input
              className="input"
              type={this.props.disableLoginFields ? 'password' : 'text'}
              id="device-id"
              value={this.props.disableLoginFields ?
                'DEVICEID' : (this.state.value.deviceID || '')
              }
              disabled={this.props.disableLoginFields}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    deviceID: event.target.value,
                  },
                }));
              }}
            />
          </p>
        </div>
        <div className="control is-grouped">
          <p className="control is-expanded">
            <label className="label" htmlFor="location">Latitude / Longitude</label>
            <InputLocation
              id="location"
              value={[this.state.value.longitude, this.state.value.latitude]}
              className={classnames({ 'is-danger': !this.state.locationValid })}
              // POI ID: IT.03SS0M9D7XP4B
              placeholder="1.28105,103.84853"
              ref={(value) => { this.location = value; }}
              onBeforeChange={() => {
                this.setState({ locationValid: this.location.isValid() });
              }}
              onChange={(value) => {
                this.setState({
                  value: _.merge(this.state.value, {
                    longitude: value[0],
                    latitude: value[1],
                  }),
                });
              }}
            />
          </p>
        </div>
        <label className="label" htmlFor="behaviour">Behaviour</label>
        <p className="control">
          <span className="select">
            <select
              id="behaviour"
              value={behaviourType}
              onChange={(event) => {
                this.setState(_.merge(this.state, {
                  value: {
                    behaviour: {
                      type: event.target.value,
                    },
                  },
                }));
              }}
            >
              {DRIVER_BEHAVIOUR_TYPES.map(behaviour => (
                <option key={behaviour.value} value={behaviour.value}>
                  {behaviour.name}
                </option>
              ))}
            </select>
          </span>
        </p>
        <div>
          <label className="label" htmlFor="behaviour-parameters">Behaviour Parameters</label>
          {DRIVER_BEHAVIOUR_PARAMS[behaviourType].map((field) => {
            switch (field.type) {
              case 'number':
                return (
                  <div className="columns" key={field.name}>
                    <div className="column is-one-third">
                      <label className="label" htmlFor={field.name}>
                        <code>{field.name}</code>
                      </label>
                    </div>
                    <div className="column is-two-thirds">
                      <input
                        className="input"
                        id={field.name}
                        type={DRIVER_BEHAVIOUR_PARAMS[behaviourType][field.name]}
                        placeholder={field.hint || _.startCase(field.name)}
                        value={_.get(this.state.value, `behaviour.params.${field.name}`, '')}
                        onChange={(event) => {
                          this.setState(_.merge(this.state, {
                            value: {
                              behaviour: {
                                params: {
                                  [field.name]: event.target.value,
                                },
                              },
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                );
              case 'boolean':
                return (
                  <div className="columns" key={field.name}>
                    <div className="column is-one-third">
                      <p className="control">
                        <label className="checkbox" htmlFor={field.name}>
                          <code>{field.name}</code>
                        </label>
                      </p>
                    </div>
                    <div className="column is-two-thirds">
                      <input
                        type="checkbox"
                        id={field.name}
                        checked={_.get(this.state.value, `behaviour.params.${field.name}`)}
                        onChange={(event) => {
                          this.setState(_.merge(this.state, {
                            value: {
                              behaviour: {
                                params: {
                                  [field.name]: !!event.target.checked,
                                },
                              },
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/js/components/entities/DriverForm.jsx
 **/